import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from '@heroicons/react/20/solid';
import ReactDOM from 'react-dom';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function DateSelector({ selectedDate, onChange, className }) {
  const [isOpen, setIsOpen] = useState(false);
  const [month, setMonth] = useState(selectedDate ? new Date(selectedDate) : new Date());
  const [days, setDays] = useState([]);
  const [inputValue, setInputValue] = useState(selectedDate || '');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const calendarRef = useRef(null);
  const inputRef = useRef(null);

  // Helper function to create date in local timezone (avoiding timezone shifts)
  const createLocalDate = (year, month, day = 1) => {
    const date = new Date();
    date.setFullYear(year);
    date.setMonth(month);
    date.setDate(day);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  // Convert string date to local date object
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return createLocalDate(year, month - 1, day);
  };

  // Format date object to YYYY-MM-DD string
  const formatDateString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Generate calendar days for the current month
  const generateDays = (currentMonth) => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      
      // Get first day of month and last day of month
      const firstDay = createLocalDate(year, month, 1);
      const lastDay = new Date(year, month + 1, 0); // Last day of current month
      
      // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
      let firstDayOfWeek = firstDay.getDay();
      // Adjust for Monday as first day of week
      firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
      
      // Generate previous month days
      const prevMonthDays = [];
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      
      for (let i = prevMonthLastDay - firstDayOfWeek + 1; i <= prevMonthLastDay; i++) {
        const date = createLocalDate(year, month - 1, i);
        prevMonthDays.push({
          date: formatDateString(date),
          isCurrentMonth: false
        });
      }
      
      // Generate current month days
      const currentMonthDays = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      for (let i = 1; i <= lastDay.getDate(); i++) {
        const date = createLocalDate(year, month, i);
        const dateString = formatDateString(date);
        
        currentMonthDays.push({
          date: dateString,
          isCurrentMonth: true,
          isToday: date.toDateString() === today.toDateString(),
          isSelected: selectedDate && dateString === selectedDate
        });
      }
      
      // Generate next month days
      const nextMonthDays = [];
      const totalDays = prevMonthDays.length + currentMonthDays.length;
      const remainingDays = 42 - totalDays; // 6 rows of 7 days
      
      for (let i = 1; i <= remainingDays; i++) {
        const date = createLocalDate(year, month + 1, i);
        nextMonthDays.push({
          date: formatDateString(date),
          isCurrentMonth: false
        });
      }
      
      setDays([...prevMonthDays, ...currentMonthDays, ...nextMonthDays]);
    } catch (error) {
      console.error('Error generating calendar days:', error);
      // Use current date as fallback
      setMonth(new Date());
    }
  };
  
  // Update month and regenerate days when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      try {
        const selectedDateObj = parseLocalDate(selectedDate);
        if (selectedDateObj) {
          setMonth(selectedDateObj);
          setInputValue(selectedDate);
        }
      } catch (error) {
        console.error('Invalid date:', error);
      }
    }
    generateDays(month);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Regenerate days when month changes
  useEffect(() => {
    generateDays(month);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  // Position the calendar popup when opened
  useEffect(() => {
    const updatePosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom + 4,
          left: rect.left
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
    }

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (isOpen && 
          inputRef.current && 
          !inputRef.current.contains(event.target) &&
          calendarRef.current && 
          !calendarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const previousMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() - 1));
  };

  const nextMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() + 1));
  };

  const handleDayClick = (day) => {
    onChange(day.date);
    setInputValue(day.date);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Only update parent if value is empty or matches date format
    if (value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      onChange(value);
    }
  };

  const toggleCalendar = () => {
    setIsOpen(!isOpen);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Create calendar using portal to avoid stacking context issues
  const CalendarPopup = () => {
    const portalRoot = document.body;
    
    if (!isOpen) return null;
    
    const content = (
      <div 
        ref={calendarRef} 
        className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 w-64"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`
        }}
      >
        <div className="flex items-center justify-between text-gray-900 dark:text-gray-100">
          <button
            type="button"
            onClick={previousMonth}
            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <span className="sr-only">Previous month</span>
            <ChevronLeftIcon className="size-5" aria-hidden="true" />
          </button>
          <div className="flex-auto text-center text-sm font-semibold">
            {monthNames[month.getMonth()]} {month.getFullYear()}
          </div>
          <button
            type="button"
            onClick={nextMonth}
            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <span className="sr-only">Next month</span>
            <ChevronRightIcon className="size-5" aria-hidden="true" />
          </button>
        </div>
        <div className="mt-4 grid grid-cols-7 text-xs/6 text-gray-500 dark:text-gray-400 text-center">
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
          <div>S</div>
        </div>
        <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 dark:bg-gray-600 text-sm shadow-sm ring-1 ring-gray-200 dark:ring-gray-600">
          {days.map((day, dayIdx) => (
            <button
              key={day.date}
              type="button"
              onClick={() => handleDayClick(day)}
              className={classNames(
                'py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 focus:z-10',
                day.isCurrentMonth ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700',
                (day.isSelected || day.isToday) && 'font-semibold',
                day.isSelected && 'text-white',
                !day.isSelected && day.isCurrentMonth && !day.isToday && 'text-gray-900 dark:text-gray-100',
                !day.isSelected && !day.isCurrentMonth && !day.isToday && 'text-gray-400 dark:text-gray-500',
                day.isToday && !day.isSelected && 'text-indigo-600 dark:text-indigo-400',
                dayIdx === 0 && 'rounded-tl-lg',
                dayIdx === 6 && 'rounded-tr-lg',
                dayIdx === days.length - 7 && 'rounded-bl-lg',
                dayIdx === days.length - 1 && 'rounded-br-lg',
              )}
            >
              <time
                dateTime={day.date}
                className={classNames(
                  'mx-auto flex size-7 items-center justify-center rounded-full',
                  day.isSelected && day.isToday && 'bg-indigo-600',
                  day.isSelected && !day.isToday && 'bg-gray-900 dark:bg-indigo-600',
                )}
              >
                {day.date.split('-').pop().replace(/^0/, '')}
              </time>
            </button>
          ))}
        </div>
      </div>
    );
    
    return ReactDOM.createPortal(content, portalRoot);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onClick={() => setIsOpen(true)}
          className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
          placeholder="YYYY-MM-DD"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer" onClick={toggleCalendar}>
          <CalendarIcon className="size-5 text-gray-400" />
        </div>
      </div>
      
      <CalendarPopup />
    </div>
  );
}